import {
  getLegendValues,
  getWmtsDescribeDomains,
  getWmtsGetDomainValues,
  getCapabilities
} from '@/api/services';
import { transformExtent } from 'ol/proj';

// --------------------- STATE -----------------------------

export const state = {
  legendArray: {},
  rasterLegend_selectedLabel: [],
  vectorLegend_selectedLabel: {},
  geographicDomains: {},
  isNewData: [],
  isRuntime: false,
  xml_string: '',
  xml_string_GDV: ''
};

// --------------------- MUTATIONS ----------------------------

export const mutations = {
  // GEOSERVER
  SET_WMTS_RESP(state, data) {
    state.xml_string = data;
  },
  SET_WMTS_GDV_RESP(state, data) {
    state.xml_string_GDV = data;
  },
  SET_CHECK_NEW_DATA_RESP(state, data) {
    state.isNewData = data;
  },
  SET_CHECK_RUNTIME_DATA_RESP(state, data) {
    state.isRuntime = data;
  },
  SET_GEOGRAPHIC_DOMAIN(state, data) {
    const [service_id, extent] = data;
    state.geographicDomains[service_id] = [];
    state.geographicDomains[service_id] = extent;
  },
  SET_LEGEND(state, { layerName, rules, layerType }) {
    state.legendArray[layerName] = {
      rules: rules,
      layer_type: layerType
    };
  }
};
// ------------------------ ACTIONS ---------------------------

export const actions = {
  // GEOSERVER
  async getLegend({ state, dispatch, commit }, data) {
    try {
      if (state.legendArray[data.layer_name]) {
        return state.legendArray[data.layer_name];
      }

      const resp = await getLegendValues(data.layer_name, data.layer_style);

      commit('SET_LEGEND', {
        layerName: data.layer_name,
        rules: resp.data.Legend[0].rules || undefined,
        layerType: data.layer_type
      });

      return state.legendArray[data.layer_name];
    } catch (err) {
      dispatch(
        'storeToastMessage',
        {
          text: 'getLegend',
          errObj: {
            errors: [{ message: 'Error while fetching legend. Reload the Raster Layer.' }]
          },
          type: 'danger'
        },
        { root: true }
      );
    }
  },

  async getWmtsArray({ commit, dispatch }, data) {
    try {
      const { layer, time, service_id, policy_id } = data;
      const resp = await getWmtsDescribeDomains(layer, time, service_id, policy_id);
      commit('SET_WMTS_RESP', resp.data);
    } catch (err) {
      dispatch(
        'storeToastMessage',
        {
          text: 'getWmtsArray',
          errObj: {
            errors: [{ message: 'Error while fetching getWmtsArray. Reload the Layer.' }]
          },
          type: 'danger'
        },
        { root: true }
      );
    }
  },

  async getWmtsGDV({ commit, dispatch }, data) {
    try {
      const [layer, time, limit] = data;
      const resp = await getWmtsGetDomainValues('time', layer, time, limit);
      commit('SET_WMTS_GDV_RESP', resp.data);
    } catch (err) {
      dispatch(
        'storeToastMessage',
        {
          text: 'getWmtsGDV',
          errObj: {
            errors: [{ message: 'Error while fetching getWmtsGDV. Reload the Layer.' }]
          },
          type: 'danger'
        },
        { root: true }
      );
    }
  },

  async checkNewData({ commit, dispatch }, data) {
    try {
      const [domain, layer, time] = data;
      const resp = await getWmtsGetDomainValues(domain, layer, time, 1);

      const xml_document = (new window.DOMParser()).parseFromString(resp.data, 'text/xml');
      const xml_content = xml_document.getElementsByTagName('Domain')[0].textContent;
      const wmtsResultArray = xml_content ? xml_content.split(',') : [];

      if (domain === 'time') {
        commit('SET_CHECK_NEW_DATA_RESP', [wmtsResultArray.length > 0, wmtsResultArray[0]]);
      }

      if (domain === 'RUNTIME') {
        commit('SET_CHECK_RUNTIME_DATA_RESP', wmtsResultArray.length > 0);

        return wmtsResultArray[0] || time;
      }
    } catch (err) {
      dispatch(
        'storeToastMessage',
        {
          text: 'checkNewData',
          errObj: {
            errors: [{ message: 'Error while checking new data. Reload the Layer.' }]
          },
          type: 'danger'
        },
        { root: true }
      );
    }
  },

  async getLayerExtent({ commit, dispatch }, data) {
    const transform = (extent) => {
      const maxWebMercatorLatitude = 85.0511287798066;
      const normalizedExtent = [
        Math.max(-180, extent[0]),
        Math.max(-maxWebMercatorLatitude, extent[1]),
        Math.min(180, extent[2]),
        Math.min(maxWebMercatorLatitude, extent[3])
      ];

      return transformExtent(normalizedExtent, 'EPSG:4326', 'EPSG:3857');
    };

    try {
      const extent = [];
      const [ws, layer, type, service_id] = data;
      const resp = await getCapabilities(ws, layer, type);

      const xml_document = (new window.DOMParser()).parseFromString(resp.data, 'text/xml');
      const xml_for_vector = xml_document.getElementsByTagName('FeatureType');

      if (type === 'VECTOR') {
        xml_for_vector.forEach(item => {
          if (item.getElementsByTagName('Name')[0].innerHTML === `${ws}:${layer}`) {
            const extentNode = item.getElementsByTagName('ows:WGS84BoundingBox')[0].children;

            extentNode.forEach(element => {
              extent.push(parseFloat(element.textContent.split(' ')[0]));
              extent.push(parseFloat(element.textContent.split(' ')[1]));
            });
          }
        });
      } else {
        extent.push(parseFloat(xml_document.getElementsByTagName('westBoundLongitude')[0].textContent));
        extent.push(parseFloat(xml_document.getElementsByTagName('southBoundLatitude')[0].textContent));
        extent.push(parseFloat(xml_document.getElementsByTagName('eastBoundLongitude')[0].textContent));
        extent.push(parseFloat(xml_document.getElementsByTagName('northBoundLatitude')[0].textContent));
      }

      commit('SET_GEOGRAPHIC_DOMAIN', [service_id, transform(extent)]);

      return transform(extent);
    } catch (err) {
      dispatch(
        'storeToastMessage',
        {
          text: 'getLayerExtent',
          errObj: {
            errors: [{ message: 'Error while fetching getLayerExtent. Reload the Layer.' }]
          },
          type: 'danger'
        },
        { root: true }
      );
    }
  }
};

export const getters = {};
